/* The homepage's editorial layer. Native scrolling and server-rendered content
   remain usable when JavaScript is unavailable. No commerce state is changed. */
(() => {
  'use strict';
  const motion = matchMedia('(prefers-reduced-motion: reduce)');
  const main = document.getElementById('MainContent');
  if (!main) return;
  const storySelector = '[data-concept-entrance], [data-hope-entrance], [data-design-entrance]';
  const seenStories = new WeakSet();

  function animatePhotograph(scene, media, image) {
    const animations = [];
    const design = scene.hasAttribute('data-design-entrance');
    const mobile = innerWidth <= 768;
    let photoFrames = [
      { clipPath: 'inset(100% 0 0 0)' },
      { clipPath: 'inset(0% 0 0 0)' }
    ];
    let photoDuration = 1450;
    let photoEasing = 'cubic-bezier(.55,0,.2,1)';
    let photoScale = mobile ? 1.035 : 1.065;
    let settleDuration = 2400;
    const quietNight = design && scene.classList.contains('design-scene--night');
    if (design) {
      if (scene.classList.contains('design-scene--craft')) {
        // Follow the handwork across the frame, from left to right.
        photoFrames = [{ clipPath: 'inset(0 100% 0 0)' }, { clipPath: 'inset(0 0% 0 0)' }];
        photoDuration = 1600;
        photoScale = mobile ? 1.025 : 1.045;
      } else if (quietNight) {
        // Let the still life rise gently into place without another zoom.
        photoFrames = [
          { opacity: 0, transform: `translateY(${mobile ? 16 : 28}px)` },
          { opacity: getComputedStyle(media).opacity, transform: 'translateY(0)' }
        ];
        photoDuration = 1800;
        photoEasing = 'cubic-bezier(.22,1,.36,1)';
      } else {
        // Pull back from the close-up to reveal the complete glass and ingredients.
        photoFrames = [{ opacity: 0 }, { opacity: getComputedStyle(media).opacity }];
        photoDuration = 1100;
        photoEasing = 'cubic-bezier(.4,0,.2,1)';
        photoScale = mobile ? 1.075 : 1.14;
        settleDuration = 2600;
      }
    }
    animations.push(media.animate(photoFrames, {
      duration: photoDuration,
      easing: photoEasing,
      fill: 'backwards'
    }));
    if (!quietNight) {
      animations.push(image.animate([
        { transform: `scale(${photoScale})` },
        { transform: getComputedStyle(image).transform }
      ], { duration: settleDuration, easing: 'cubic-bezier(.2,.65,.3,1)', fill: 'backwards' }));
    }
    return { animations };
  }

  // Photographs and titles enter together without interrupting scrolling.
  // Offscreen mobile content still waits for its own visible reading position.
  function mountPairedStory(scene, media, image) {
    const design = scene.hasAttribute('data-design-entrance');
    const mobile = innerWidth <= 768;
    const top = scene.querySelector(design ? '.design-scene__top' : '.iwt__top');
    const detail = scene.querySelector(design ? '.design-scene__detail' : '.iwt__bottom');
    const spread = design ? scene : scene.querySelector('.iwt');
    const stages = [media, top, detail].filter(Boolean).map(element => ({ element, state: 'waiting', animations: [], timer: 0 }));
    const events = new AbortController();
    const root = document.documentElement;
    let stopped = false, frame = 0, imageTimer = 0, imageExpired = false, detailTimer = 0;
    let headingReleased = false, detailReady = false;
    const observer = new IntersectionObserver(check, { threshold: 0 });
    const openingObserver = new MutationObserver(check);
    function complete(stage) {
      if (!stage || stage.state === 'done') return;
      stage.state = 'done';
      clearTimeout(stage.timer);
      stage.animations.forEach(animation => animation.cancel());
      stage.element.classList.remove('story-part-waiting');
      if (stages.every(item => item.state === 'done')) finish();
    }
    function finish() {
      if (stopped) return;
      stopped = true;
      seenStories.add(scene);
      observer.disconnect();
      openingObserver.disconnect();
      events.abort();
      cancelAnimationFrame(frame);
      clearTimeout(imageTimer);
      clearTimeout(detailTimer);
      stages.forEach(complete);
    }
    function play(stage) {
      if (!stage || stopped || stage.state !== 'waiting') return;
      stage.state = 'running';
      stage.element.classList.remove('story-part-waiting');
      stage.timer = setTimeout(() => complete(stage), 5000);
      try {
        stage.animations = stage.element === media
          ? animatePhotograph(scene, media, image).animations
          : [stage.element.animate([
            { opacity: 0, transform: `translateY(${stage.element === top ? 12 : 8}px)` },
            { opacity: getComputedStyle(stage.element).opacity, transform: 'translateY(0)' }
          ], { duration: stage.element === top ? 1000 : 800, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' })];
        Promise.all(stage.animations.map(animation => animation.finished)).then(() => complete(stage), () => complete(stage));
      } catch (_) { complete(stage); }
    }
    function releaseHeading(animate) {
      if (stopped || headingReleased) return;
      clearTimeout(imageTimer);
      imageTimer = 0;
      headingReleased = true;
      const stage = stages.find(item => item.element === top);
      if (animate) play(stage);
      else complete(stage);
      // On phones the description still waits for its own viewport entry.
      if (animate) detailTimer = setTimeout(() => { detailReady = true; check(); }, mobile ? 180 : 450);
      else detailReady = true;
      check();
    }
    function check() {
      if (stopped) return;
      if (!scene.isConnected || window.visualViewport?.scale > 1.01) { finish(); return; }
      if (root.matches('.hero-entrance-pending, .hero-entrance-running, .is-splashing, .design-intro-scroll-locked')) return;
      const height = window.visualViewport?.height || innerHeight;
      const header = document.querySelector('[data-header]');
      const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom || 0);
      const bar = document.querySelector('[data-purchase-bar]');
      const barRect = bar?.getBoundingClientRect();
      const bottom = Math.min(height * .88, barRect && barRect.top > 0 && barRect.top < height && getComputedStyle(bar).visibility !== 'hidden' ? barRect.top - 16 : height);
      if (!headingReleased) {
        const headingRect = top?.getBoundingClientRect();
        if (!headingRect || headingRect.bottom <= headerBottom) { releaseHeading(false); return; }
        const entry = mobile ? headingRect.top + Math.min(24, headingRect.height * .2) : spread.getBoundingClientRect().top;
        const threshold = mobile ? bottom : height * (scene.hasAttribute('data-concept-entrance') ? .82 : .62);
        if (entry <= threshold && headingRect.height > 0) {
          if (!image.complete && !imageExpired) {
            if (!imageTimer) imageTimer = setTimeout(() => { imageExpired = true; check(); }, 1800);
            return;
          }
          releaseHeading(!imageExpired && image.naturalWidth > 0);
          return;
        }
      }
      stages.forEach(stage => {
        if (stage.state !== 'waiting' || stage.element === top) return;
        const rect = stage.element.getBoundingClientRect();
        if (rect.bottom <= headerBottom) { complete(stage); return; }
        if (!rect.height) return;
        if (stage.element === detail && (!detailReady || (mobile && rect.top + 8 > bottom))) return;
        if (stage.element === media) {
          if (!headingReleased) return;
          const entry = mobile ? rect.top + 24 : spread.getBoundingClientRect().top;
          const threshold = mobile ? bottom : height * (scene.hasAttribute('data-concept-entrance') ? .82 : .62);
          if (entry > threshold || rect.bottom <= headerBottom) return;
          if (!image.complete && !imageExpired) {
            if (!imageTimer) imageTimer = setTimeout(() => { imageExpired = true; check(); }, 1800);
            return;
          }
          clearTimeout(imageTimer);
          if (imageExpired || !image.naturalWidth) { complete(stage); return; }
        }
        play(stage);
      });
    }
    function schedule() {
      if (!frame) frame = requestAnimationFrame(() => { frame = 0; check(); });
    }
    stages.forEach(stage => { stage.element.classList.add('story-part-waiting'); observer.observe(stage.element); });
    openingObserver.observe(root, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('scroll', schedule, { passive: true, signal: events.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: events.signal });
    window.visualViewport?.addEventListener('resize', schedule, { passive: true, signal: events.signal });
    image.addEventListener('load', schedule, { signal: events.signal });
    image.addEventListener('error', schedule, { signal: events.signal });
    scene.addEventListener('focusin', finish, { signal: events.signal });
    window.addEventListener('beforeprint', finish, { signal: events.signal });
    window.addEventListener('pagehide', finish, { signal: events.signal });
    window.addEventListener('pageshow', event => { if (event.persisted) finish(); }, { signal: events.signal });
    check();
    return finish;
  }

  function mountStory(scene) {
    const design = scene.hasAttribute('data-design-entrance');
    const media = scene.querySelector(design ? '.design-scene__visual' : '.iwt__media');
    const image = media?.querySelector('img');
    const spread = design ? scene : scene.querySelector('.iwt');
    if (!image || !spread || seenStories.has(scene) || motion.matches ||
        window.Shopify?.designMode || !media.animate || !('IntersectionObserver' in window)) return () => {};
    // A restored/deep-linked position is already being read; leave it visible.
    if (window.scrollY > 8 && scene.getBoundingClientRect().top < innerHeight) {
      seenStories.add(scene);
      return () => {};
    }

    return mountPairedStory(scene, media, image);
  }

  let dispose = () => {};
  function mount() {
    dispose();
    const cleanups = [];
    main.querySelectorAll(storySelector).forEach(scene => cleanups.push(mountStory(scene)));
    const header = document.querySelector('[data-header]');
    function measureHeader() {
      const bottom = header ? header.getBoundingClientRect().bottom : 0;
      document.documentElement.style.setProperty('--night-header-bottom', `${Math.max(0, bottom)}px`);
    }
    measureHeader();
    const resize = new ResizeObserver(measureHeader);
    if (header) resize.observe(header);
    cleanups.push(() => resize.disconnect());
    window.addEventListener('resize', measureHeader, { passive: true });
    cleanups.push(() => window.removeEventListener('resize', measureHeader));

    // Drink recipes are all visible in the server-rendered gallery.
    // Other editorial sections keep their existing entrances and scroll drift.
    // Concept, Our Hope and each DESIGN scene own their paired entrances.
    if (!motion.matches && 'IntersectionObserver' in window) {
      const animations = new Set();
      const outsideStory = el => !el.closest(storySelector);
      const owned = [...main.querySelectorAll('.iwt__media, .iwt__text, .feature-cards')].filter(outsideStory);
      owned.forEach(el => el.classList.add('night-motion-owned'));
      const targets = [...main.querySelectorAll('.iwt__media, .iwt__top, .iwt__body p, .feature-card')].filter(outsideStory);
      const enter = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const photo = el.classList.contains('iwt__media');
          const card = el.classList.contains('feature-card');
          const index = card ? [...el.parentElement.children].indexOf(el) : 0;
          const frames = photo
            ? [{ clipPath: 'inset(12% 5% 12% 5%)', opacity: .25 }, { clipPath: 'inset(0% 0% 0% 0%)', opacity: 1 }]
            : [{ transform: `translateY(${card ? 48 : 24}px)`, opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }];
          const animation = el.animate(frames, { duration: photo ? 1500 : 1100, delay: index * 120, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'backwards' });
          animations.add(animation);
          animation.onfinish = () => animations.delete(animation);
          enter.unobserve(el);
        });
      }, { threshold: .12, rootMargin: '0px 0px -4% 0px' });
      targets.forEach(el => enter.observe(el));
      const spreads = [...main.querySelectorAll('.iwt')].filter(outsideStory);
      let driftFrame = 0;
      const drift = () => {
        driftFrame = 0;
        spreads.forEach(spread => {
          const rect = spread.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top > innerHeight) return;
          const progress = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - innerHeight / 2) / innerHeight));
          spread.style.setProperty('--night-drift', `${innerWidth > 768 ? progress * -32 : 0}px`);
        });
      };
      const onDrift = () => { if (!driftFrame) driftFrame = requestAnimationFrame(drift); };
      window.addEventListener('scroll', onDrift, { passive: true });
      window.addEventListener('resize', onDrift, { passive: true });
      drift();
      cleanups.push(() => {
        enter.disconnect(); animations.forEach(animation => animation.cancel());
        owned.forEach(el => el.classList.remove('night-motion-owned'));
        cancelAnimationFrame(driftFrame);
        window.removeEventListener('scroll', onDrift); window.removeEventListener('resize', onDrift);
        spreads.forEach(el => el.style.removeProperty('--night-drift'));
      });
    }
    dispose = () => cleanups.forEach(fn => fn());
  }
  mount();
  motion.addEventListener('change', mount);
  matchMedia('(max-width: 768px)').addEventListener('change', mount);
  document.addEventListener('shopify:section:load', mount);
  document.addEventListener('shopify:section:unload', mount);
  document.addEventListener('shopify:section:reorder', mount);
})();
